import { type FormEvent, useCallback, useEffect, useState } from "react";

import {
  confirmNotificationReceipt,
  fetchNotificationStatus,
  type NotificationStatus,
  removeNotificationSubscription,
  sendTestNotification,
} from "./api";

type PanelState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly data: NotificationStatus }
  | { readonly status: "failed" };

const PROBLEM_TEXT: Readonly<Record<string, string>> = {
  incomplete: "Set all three Worker secrets: NTFY_BASE_URL, NTFY_TOPIC and NTFY_PUBLISH_TOKEN.",
  "base-url-invalid": "NTFY_BASE_URL must be an HTTPS origin such as https://ntfy.thearach.net, without a path or credentials.",
  "topic-invalid": "NTFY_TOPIC may contain only letters, digits, underscores and hyphens.",
  "token-invalid": "NTFY_PUBLISH_TOKEN must be an ntfy access token (tk_…).",
  "legacy-url-invalid": "The legacy NTFY_URL secret is not a valid HTTPS topic URL. Replace it with the three split secrets.",
  "legacy-secret-has-credentials": "The legacy NTFY_URL secret embeds credentials, which are never sent in a URL. Replace it with the three split secrets.",
};

/**
 * Operations panel for the private ntfy route: redacted status, an audited test publish, and
 * operator confirmation that a named device received it. Tokens and topic names are never shown.
 */
export function NotificationPanel({ csrf }: { readonly csrf: string }) {
  const [state, setState] = useState<PanelState>({ status: "loading" });
  const [announcement, setAnnouncement] = useState("");
  const [lastTestId, setLastTestId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState({ status: "ready", data: await fetchNotificationStatus() });
    } catch {
      setState({ status: "failed" });
    }
  }, []);

  useEffect(() => {
    let active = true;
    fetchNotificationStatus().then(
      (data) => {
        if (active) setState({ status: "ready", data });
      },
      () => {
        if (active) setState({ status: "failed" });
      },
    );
    return () => {
      active = false;
    };
  }, []);

  async function sendTest(): Promise<void> {
    setBusy(true);
    try {
      const result = await sendTestNotification(csrf);
      if (result.status === "rate-limited") {
        setAnnouncement("A test was sent less than a minute ago. Wait a minute, then try again.");
      } else if (result.status === "sent") {
        setLastTestId(result.id);
        setAnnouncement("Test notification accepted by ntfy. Confirm below once it arrives on your device.");
      } else {
        setAnnouncement(`Test notification failed: ${result.outcome}.`);
      }
      await load();
    } catch {
      setAnnouncement("The test could not be sent or its status could not be loaded. Check the connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmReceipt(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const label = String(form.get("clientLabel") ?? "").trim();
    const testId = String(form.get("testId") ?? "");
    try {
      await confirmNotificationReceipt(testId, label, csrf);
      setAnnouncement(`Receipt on ${label} recorded.`);
      await load();
    } catch {
      setAnnouncement("Receipt could not be recorded. Use a delivered test from the last 24 hours for the current notification route.");
    }
  }

  async function remove(id: string, label: string): Promise<void> {
    try {
      await removeNotificationSubscription(id, csrf);
      setAnnouncement(`${label} removed.`);
      await load();
    } catch {
      setAnnouncement(`${label} could not be removed.`);
    }
  }

  const deliveredTests = state.status === "ready" ? state.data.tests.filter((test) => test.outcome === "delivered") : [];
  return (
    <div className="notification-panel">
      <h3>Private ntfy notifications</h3>
      <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
      {announcement ? <p aria-hidden="true">{announcement}</p> : null}
      {state.status === "loading" ? <p role="status">Reading notification state…</p> : null}
      {state.status === "failed"
        ? <p className="error" role="alert">Notification state is unavailable. <button type="button" onClick={() => void load()}>Try again</button></p>
        : null}
      {state.status === "ready"
        ? (
          <>
            <dl>
              <dt>Route</dt>
              <dd>
                {state.data.route.status === "configured"
                  ? `${state.data.route.identity}${state.data.route.source === "legacy" ? " — legacy NTFY_URL; migrate to the split secrets" : ""}`
                  : state.data.route.status === "invalid"
                  ? PROBLEM_TEXT[state.data.route.problem] ?? "The ntfy secrets are invalid."
                  : "Not configured. Alerts are recorded in Fleet but not pushed."}
              </dd>
              <dt>Last alert delivery</dt>
              <dd>
                {state.data.lastDelivery
                  ? `${state.data.lastDelivery.state}${state.data.lastDelivery.errorCode ? ` (${state.data.lastDelivery.errorCode})` : ""} at ${state.data.lastDelivery.at}`
                  : "No alert has been sent yet."}
              </dd>
              <dt>Confirmed devices</dt>
              <dd>
                {state.data.subscriptions.length === 0
                  ? "None. A configured route does not prove any device receives it."
                  : (
                    <ul>
                      {state.data.subscriptions.map((subscription) => (
                        <li key={subscription.id}>
                          {subscription.clientLabel} · verified {subscription.verifiedAt}
                          {subscription.current ? "" : " · other notification route, re-verify after rotation"}
                          {" "}
                          <button type="button" onClick={() => void remove(subscription.id, subscription.clientLabel)}>
                            Remove {subscription.clientLabel}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
              </dd>
            </dl>
            <button type="button" disabled={busy || state.data.route.status !== "configured"} onClick={() => void sendTest()}>
              {busy ? "Sending…" : "Send test notification"}
            </button>
            {deliveredTests.length
              ? (
                <form onSubmit={(event) => void confirmReceipt(event)} aria-label="Confirm test notification receipt">
                  <label>
                    Delivered test
                    <select name="testId" defaultValue={lastTestId ?? deliveredTests[0]?.id}>
                      {deliveredTests.map((test) => (
                        <option key={test.id} value={test.id}>{test.requestedAt} ({test.id.slice(0, 8)})</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Device name
                    <input name="clientLabel" required maxLength={64} autoComplete="off" placeholder="Phone (ntfy app)" />
                  </label>
                  <button type="submit">I received it</button>
                </form>
              )
              : null}
          </>
        )
        : null}
    </div>
  );
}
